/**
 * Your Music — service shell.
 *
 * Three top tabs: Your Music · Upload Music · Membership. Before the service can be
 * used, the user must sign the one-time Music Service & Ownership Agreement (the
 * gate). ONE <audio> element + playback state live here (via useMusicPlayer) so
 * playback persists across tabs/views. "Play All" / opening a playlist / any track
 * click opens the full-page MusicNowPlaying view; while browsing, a compact mini-bar
 * (MusicPlayer) keeps playback going. All signed paperwork is in mus_legal_acceptance.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRefreshState } from '../../lib/refreshState';
import { FiMusic, FiUploadCloud, FiAward } from 'react-icons/fi';
import {
  loadMusicTables, getOwnedTracks, getCurrentUserId, logPlay, TrackView,
  migrateMusicBlobsToServer,
} from './musicLibrary';
import { getMusicEntitlement } from '../../lib/musicEntitlements';
import { confirmDiscardChanges, setFormDirty } from '../../lib/unsavedChanges';
import {
  SERVICE_AGREEMENT, hasAcceptedInitialAgreement, recordInitialAgreement, loadLegalRecords,
} from '../../lib/musicLegal';
import { warmMyProfile } from '../../lib/profileApi';
import { useMusicPlayer } from './useMusicPlayer';
import MusicLanding from './MusicLanding';
import MusicUpload from './MusicUpload';
import MusicMembership from './MusicMembership';
import MusicLegalModal from './MusicLegalModal';
import MusicNowPlaying from './MusicNowPlaying';
import MusicPlayer from './MusicPlayer';
import './MusicPage.css';

type MusicTab = 'your-music' | 'upload' | 'membership';

function sessionName(): string {
  try {
    const u = JSON.parse(localStorage.getItem('akt_session_user') || '{}');
    return [u.firstName, u.lastName].filter(Boolean).join(' ');
  } catch { return ''; }
}

export const MusicPage: React.FC = () => {
  const userId = getCurrentUserId();
  const [tab, setTab] = useRefreshState<MusicTab>('tab', 'your-music', v => ['your-music', 'upload', 'membership'].includes(v as string));
  // Guard-then-land: switching tabs must check for unsaved work (e.g. an in-progress
  // Upload Music form) before navigating away.
  const switchTab = async (next: MusicTab) => {
    if (next === tab) return;
    if (!(await confirmDiscardChanges())) return;
    setFormDirty(false);
    setTab(next);
  };
  const [tracks, setTracks] = useState<TrackView[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [version, setVersion] = useState(0);
  const [accepted, setAccepted] = useState(false);
  const [gateBusy, setGateBusy] = useState(false);

  const [playQueue, setPlayQueue] = useState<TrackView[]>([]);
  const [playIndex, setPlayIndex] = useState(-1);
  const [playTitle, setPlayTitle] = useState('Now playing');
  const [playerOpen, setPlayerOpen] = useState(false);

  const onPlayLogged = useCallback((t: TrackView) => {
    if (userId != null) logPlay(userId, t.trackId);
  }, [userId]);

  // One audio element + playback state for the whole music area.
  const player = useMusicPlayer(playQueue, playIndex, setPlayIndex, onPlayLogged);

  const refresh = useCallback(async () => {
    if (userId == null) return;
    await warmMyProfile();   // get the caller's real plan into the cache (account owners)
    await loadMusicTables();
    await loadLegalRecords();
    setTracks(getOwnedTracks(userId));
    setAccepted(hasAcceptedInitialAgreement(userId));
    setVersion(v => v + 1);
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refresh();
      if (!cancelled) setLoaded(true);
      // Best-effort: lift any device-local (idb://) song/cover blobs up to the
      // server so a browser wipe can't lose them. Non-blocking; refresh if moved.
      if (userId != null) {
        try {
          const r = await migrateMusicBlobsToServer(userId);
          if (!cancelled && (r.migrated || r.covers)) await refresh();
        } catch { /* ignore — playback still works from idb */ }
      }
    })();
    return () => { cancelled = true; };
  }, [refresh, userId]);

  const ent = useMemo(() => getMusicEntitlement(userId), [userId, version]); // eslint-disable-line react-hooks/exhaustive-deps
  const showAds = ent.adSupported;
  const planTier = ent.adSupported ? 'free' : (ent.planCode || 'free').toLowerCase();

  // Starting playback (Play All / playlist / track click) opens the full player page.
  const onPlay = useCallback((queue: TrackView[], index: number, title?: string) => {
    setPlayQueue(queue);
    setPlayIndex(index);
    setPlayTitle(title || 'Now playing');
    setPlayerOpen(true);
  }, []);

  const closePlayback = useCallback(() => {
    const el = player.audioRef.current;
    if (el) el.pause();
    setPlayerOpen(false);
    setPlayIndex(-1);
    setPlayQueue([]);
  }, [player.audioRef]);

  const currentTrackId = player.track ? player.track.trackId : null;

  const signGate = useCallback(async (signatureName: string) => {
    if (userId == null) return;
    setGateBusy(true);
    await recordInitialAgreement(userId, signatureName);
    await loadLegalRecords();
    setGateBusy(false);
    setAccepted(true);
  }, [userId]);

  if (userId == null) {
    return <div className="music-page"><p className="music-empty">Please log in to use Your Music.</p></div>;
  }

  return (
    <div className={`music-page ${(!playerOpen && player.hasTrack) ? 'has-player' : ''}`}>
      {/* The single audio element — stays mounted across views so playback persists. */}
      <audio
        ref={player.audioRef}
        style={{ display: 'none' }}
        onTimeUpdate={player.onTimeUpdate}
        onLoadedMetadata={player.onLoadedMetadata}
        onEnded={player.onEnded}
        onPlay={player.onPlay}
        onPause={player.onPause}
      />

      {playerOpen ? (
        <MusicNowPlaying
          player={player}
          queue={playQueue}
          index={playIndex}
          setIndex={setPlayIndex}
          title={playTitle}
          onBack={() => setPlayerOpen(false)}
          onHome={() => { setPlayerOpen(false); setTab('your-music'); }}
          onChanged={refresh}
        />
      ) : (
        <>
          {/* Header */}
          <div className="music-header">
            <div className="music-title-row">
              <FiMusic className="music-title-icon" />
              <h2>Your Music</h2>
            </div>
          </div>

          {/* Top tabs */}
          <div className="music-tabs">
            <button className={`music-tab ${tab === 'your-music' ? 'active' : ''}`} onClick={() => switchTab('your-music')}>
              <FiMusic /> Your Music {tracks.length > 0 && <span className="music-tab-count">{tracks.length}</span>}
            </button>
            <button className={`music-tab ${tab === 'upload' ? 'active' : ''}`} onClick={() => switchTab('upload')}>
              <FiUploadCloud /> Upload Music
            </button>
            <button className={`music-tab ${tab === 'membership' ? 'active' : ''}`} onClick={() => switchTab('membership')}>
              <FiAward /> Membership
            </button>
          </div>

          {/* Content (only once the agreement is signed) */}
          {!loaded ? (
            <p className="music-empty">Loading your music…</p>
          ) : !accepted ? (
            <div className="music-gate-hint">
              <p>Please review and accept the Music Service &amp; Ownership Agreement to continue.</p>
            </div>
          ) : (
            <>
              {tab === 'your-music' && (
                <MusicLanding
                  tracks={tracks}
                  userId={userId}
                  onPlay={onPlay}
                  currentTrackId={currentTrackId}
                  onChanged={refresh}
                  showAds={showAds}
                  planTier={planTier}
                  goUpload={() => switchTab('upload')}
                />
              )}
              {tab === 'upload' && (
                <MusicUpload userId={userId} signatureDefault={sessionName()} onUploaded={refresh} />
              )}
              {tab === 'membership' && (
                <MusicMembership userId={userId} onChanged={refresh} />
              )}
            </>
          )}

          {/* Compact mini-bar while browsing (keeps playback going). */}
          {player.hasTrack && (
            <MusicPlayer player={player} onExpand={() => setPlayerOpen(true)} onClose={closePlayback} />
          )}
        </>
      )}

      {/* One-time service agreement gate */}
      {loaded && !accepted && (
        <MusicLegalModal
          doc={SERVICE_AGREEMENT}
          intro="Welcome to Akountant Music — your personal locker for the music you own."
          confirmLabel="I agree — enter my library"
          cancelLabel="Maybe later"
          busy={gateBusy}
          defaultSignature={sessionName()}
          onAccept={signGate}
          onCancel={() => { /* stay gated; nothing to do */ }}
        />
      )}
    </div>
  );
};

export default MusicPage;
